import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { type SceneCategory, type SceneGroup, VideoData } from '@lumen5/luminary';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { addedSceneBaseSceneNote } from './base-scene-note';

import { describeCoveredWords, resolveOverlayRangeOperation } from './overlay-placement';
import { isMotionGraphicsSceneRequested } from './motion-graphics/motionGraphicsSceneDesign';
import { computeSceneShortIds } from './scene-addressing';
import { resolveSceneGroupById, resolveSceneGroupHandle } from './scene-group-addressing';
import {
    isSceneIdReference,
    loadSceneCopy,
    resolveSceneCopySource,
    type SceneCopySource,
} from './scene-copy';
import { validateSceneDesignId } from './scene-design-validation';
import { getInterleavedTranscript, interleavedTokenToString } from './transcript-tokens';
import { VideoStateTool } from './VideoStateTool';

import { getTemplateDefaultCutawaySceneDesign } from 'js/src/data/templates';
import {
    getTemplateDefaultTalkingHeadSceneDesign,
    getTemplateDefaultVoiceoverSceneDesign,
} from 'js/src/data/templates/templates-selectors';
import { checkpointVideo } from 'js/src/data/video-data';
import { refreshFirstScenePreviewOperation } from 'js/src/data/video-planner';
import type { RootState } from 'js/src/libs/RootState';
import { saveRawLuminary } from 'js/src/luminary';
import { createCodeoOverlayAtTranscriptRangeOperation } from 'js/src/luminary/html-asset-operations';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { moveOtherOverlaysAndCreateOverlayOperation } from 'js/src/luminary/luminary-operations';
import {
    getBaseSceneDesignId,
    getOverlayScenesInSceneGroup,
    isAIAvatarSceneGroup,
    isOverlaySceneDesign,
    isVoiceoverSceneGroup,
} from 'js/src/luminary/luminary-selectors';

export const addVoiceoverSceneSchema = z.object({
    scene_group_id: z
        .string()
        .describe(
            'The stable scene_group_id of the scene group to add the scene to (from the video state).',
        ),
    scenes: z
        .array(
            z
                .object({
                    words: z
                        .array(z.string())
                        .min(1)
                        .optional()
                        .describe(
                            'Array of consecutive words from the transcript this scene should cover. These must appear sequentially in the transcript exactly as spelled, including any `<pause seconds="N"/>` tag that falls between them (write each pause exactly as it appears in the transcript). Omit when using cover_whole_group.',
                        ),
                    cover_whole_group: z
                        .boolean()
                        .optional()
                        .describe(
                            'Cover the entire scene group with this one scene, from the first frame to the end, without listing every transcript token — the way to place a full-recording audiogram/waveform. Only valid while the group still has exactly one scene (its auto-generated base); once other overlays exist it is refused, so it can never blow them away. Mutually exclusive with words.',
                        ),
                    scene_design_id: z
                        .string()
                        .describe(
                            `The ID of the scene design to use for this scene (e.g., "minimal:text_1"), OR the scene_id of a scene to copy, from this video or from a reference video.`,
                        ),
                })
                .refine(
                    (scene) =>
                        (scene.words !== undefined && scene.words.length > 0) !==
                        (scene.cover_whole_group === true),
                    {
                        message:
                            'Provide exactly one of `words` or `cover_whole_group` for each scene.',
                    },
                ),
        )
        .min(1)
        .describe(
            'The scenes to add. Each covers its own non-overlapping range of the transcript; add every scene for this scene group in one call rather than one call per scene.',
        ),
});
export type AddVoiceoverSceneArgs = z.infer<typeof addVoiceoverSceneSchema>;

export class AddVoiceoverSceneTool extends VideoStateTool<typeof addVoiceoverSceneSchema> {
    protected override readonly schema = addVoiceoverSceneSchema;
    protected override readonly toolName = TOOL_NAMES.addVoiceoverScene;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription = `Add a new scene to a voiceover or talking-head scene group, over specific sequences of transcript words. A range starting at the first transcript token also covers the leading silence before it, and one ending at the last token runs to the end — so a full-video treatment (e.g. a waveform/audiogram design) is created with \`cover_whole_group: true\` (preferred, and the only reliable way for a long recording) rather than by passing every token of the transcript. Use ${TOOL_NAMES.listSceneDesigns} first to find a suitable design (e.g. a lower third), passing narrative_thread="talking_head" for a talking-head scene group or narrative_thread="voiceover" for a voiceover scene group. Choose ${TOOL_NAMES.addVoiceoverScene} when the audio structure should stay the same, the user wants emphasis on specific words, or you're adding a visual moment during speech. (To create a structural break in the narration instead, use ${TOOL_NAMES.splitSceneGroup}.)`;

    public override getToolDisplayName(toolCallArgs?: string): string {
        if (toolCallArgs) {
            try {
                const parsed = JSON.parse(toolCallArgs) as Partial<AddVoiceoverSceneArgs>;
                if ((parsed.scenes?.length ?? 0) > 1) {
                    return `Adding ${parsed.scenes!.length} voiceover scenes`;
                }
            } catch {
                // fall through to the singular name
            }
        }
        return 'Adding voiceover scene';
    }

    protected override onEditApplied(args: AddVoiceoverSceneArgs): void {
        // A scene added to the first scene group renders over the first scene, so it changes the
        // thumbnail. Resolve defensively — this runs after a successful edit, so it must never throw.
        const resolved = resolveSceneGroupById(this.luminary, args.scene_group_id);
        if (!('error' in resolved) && resolved.index === 0) {
            this.dispatch(refreshFirstScenePreviewOperation());
        }
    }

    public get statusIcon(): string {
        return 'layers';
    }

    /**
     * The motion-graphics branch: lay an empty graphic over the resolved words, built on the
     * template's default cutaway and then cleared. The design tool authors it afterwards.
     */
    private addMotionGraphicsScene(
        sceneGroupId: string,
        startTimeMillis: number,
        endTimeMillis: number,
        templateId: string,
        parentActionGroup: string,
    ): { sceneId: string; createdBaseScenes: boolean } | { error: string } {
        const cutawaySceneDesignId = getTemplateDefaultCutawaySceneDesign(
            this.getState(),
            templateId,
        );
        if (!cutawaySceneDesignId) {
            return {
                error: `template "${templateId}" has no default cutaway scene design, so a motion graphics scene can't be added to a voice-driven scene group.`,
            };
        }

        const { createCodeoOverlayAtTranscriptRange } = makeOperationsCallable(
            { createCodeoOverlayAtTranscriptRangeOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const sceneIdsBefore = this.snapshotSceneIds();
        const created = createCodeoOverlayAtTranscriptRange(
            sceneGroupId,
            startTimeMillis,
            endTimeMillis,
            cutawaySceneDesignId,
            false,
            parentActionGroup,
        );
        if (!created) {
            return {
                error: `could not add a motion graphics scene to scene group ${sceneGroupId}.`,
            };
        }

        this.startMotionGraphicsSceneBuilding(created.sceneId);
        this.blurScenesCreatedSince(sceneIdsBefore, [created.sceneId]);

        return {
            sceneId: created.sceneId,
            createdBaseScenes: this.someSceneCreatedSince(sceneIdsBefore, created.sceneId),
        };
    }

    /**
     * Validate and place one requested scene. Returns the new scene's id, or the reason it was
     * refused so the batch can report it without abandoning the scenes that did land.
     */
    private addOneScene(
        sceneGroup: SceneGroup,
        words: string[],
        sceneDesignId: string,
        parentActionGroup: string,
        copySource?: SceneCopySource,
    ):
        | { sceneId: string; isMotionGraphics: boolean; createdBaseScenes: boolean }
        | { error: string } {
        const state = this.getState();
        const currentTemplateId = this.luminary.template.id;

        // A copy of a base scene is placed on the default cutaway design: the design id alone decides
        // whether the group treats a scene as an overlay, so keeping the base design would fold the
        // copy into the base instead of giving it its own span. The copy then brings the base's
        // content — for a talking head, the speaker and their framing.
        const placementDesignId =
            copySource && sceneDesignId === getBaseSceneDesignId(state, sceneGroup)
                ? (getTemplateDefaultCutawaySceneDesign(state, currentTemplateId) ?? sceneDesignId)
                : sceneDesignId;

        // A motion graphics scene brings no template design to vet — it is created from the
        // default cutaway and then cleared — so the design-compatibility rules below don't apply.
        const isMotionGraphicsScene = isMotionGraphicsSceneRequested(placementDesignId);
        if (!isMotionGraphicsScene) {
            const validation = validateSceneDesignId(this.luminary, placementDesignId);
            if (validation.error !== undefined) {
                return { error: validation.error.replace(/^Error:\s*/, '') };
            }

            // Added scenes inherit the design's category, and only voice-driven compatible
            // categories are recognized as scenes that can be added this way (and accepted
            // as base scenes by future scene creation), so reject incompatible designs
            // upfront — same rule the OverlaySelector UI applies when offering these designs.
            const designCategory: SceneCategory = validation.sceneCategory;

            const voiceDrivenDefaultSceneDesignIds = {
                defaultTalkingHeadSceneDesignId: getTemplateDefaultTalkingHeadSceneDesign(
                    state,
                    currentTemplateId,
                ),
                defaultVoiceoverSceneDesignId: getTemplateDefaultVoiceoverSceneDesign(
                    state,
                    currentTemplateId,
                ),
            };
            if (
                !isOverlaySceneDesign(
                    designCategory,
                    placementDesignId,
                    voiceDrivenDefaultSceneDesignIds,
                )
            ) {
                const isBaseDesign =
                    placementDesignId ===
                        voiceDrivenDefaultSceneDesignIds.defaultTalkingHeadSceneDesignId ||
                    placementDesignId ===
                        voiceDrivenDefaultSceneDesignIds.defaultVoiceoverSceneDesignId;
                const baseScene = sceneGroup.scenes.find(
                    (scene) => scene.sceneDesign === placementDesignId,
                );
                const baseSceneShortId = baseScene
                    ? computeSceneShortIds(this.luminary.scenes.map((scene) => scene.id)).get(
                          baseScene.id,
                      )
                    : undefined;
                const copyHint = baseSceneShortId
                    ? ` To keep showing what the base scene shows over these words — say, for a graphic laid over the speaker — pass its scene_id "${baseSceneShortId}" as the scene_design_id, which copies it.`
                    : '';
                const speakerCropDesignId = getTemplateDefaultCutawaySceneDesign(
                    state,
                    currentTemplateId,
                );
                const speakerCropHint =
                    !isVoiceoverSceneGroup(sceneGroup) && speakerCropDesignId
                        ? ` It already shows the speaker wherever no other scene covers the words. To show the speaker with a different crop, add a scene with "${speakerCropDesignId}" (one full-frame media slot) and frame its media with ${TOOL_NAMES.editMedia} frame_speaker; to show several speakers at once, use a design with several media slots.`
                        : '';
                return {
                    error: isBaseDesign
                        ? `Scene design "${placementDesignId}" is the base scene design of voice-driven scene groups and cannot be used for an added scene.${speakerCropHint}${copyHint} Otherwise use ${TOOL_NAMES.listSceneDesigns} and choose a different design.`
                        : `Scene design "${placementDesignId}" (category "${designCategory}") cannot be used for a scene added to a voice-driven scene group. Use ${TOOL_NAMES.listSceneDesigns} and choose a design with a compatible category.`,
                };
            }
            if (
                isVoiceoverSceneGroup(sceneGroup) &&
                !isAIAvatarSceneGroup(sceneGroup) &&
                designCategory === VideoData.CATEGORY_TALKING_HEAD_INSERT
            ) {
                return {
                    error: `Scene design "${placementDesignId}" is a talking-head insert design and cannot be used for a scene added to a voiceover scene group. Use ${TOOL_NAMES.listSceneDesigns} and choose a different design.`,
                };
            }
        }

        const { resolveOverlayRange } = makeOperationsCallable(
            { resolveOverlayRangeOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );
        const range = resolveOverlayRange(sceneGroup, words);
        if ('error' in range) {
            return { error: range.error };
        }
        const { startTimeMillis, endTimeMillis } = range;

        if (isMotionGraphicsScene) {
            const created = this.addMotionGraphicsScene(
                sceneGroup.id,
                startTimeMillis,
                endTimeMillis,
                currentTemplateId,
                parentActionGroup,
            );
            return 'error' in created ? created : { ...created, isMotionGraphics: true };
        }

        const { moveOtherOverlaysAndCreateOverlay } = makeOperationsCallable(
            { moveOtherOverlaysAndCreateOverlayOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        const sceneIdsBefore = this.snapshotSceneIds();
        const newSceneId = moveOtherOverlaysAndCreateOverlay(
            sceneGroup.id,
            startTimeMillis,
            endTimeMillis,
            placementDesignId,
            false, // systemTriggered
            parentActionGroup,
        );
        if (copySource) {
            loadSceneCopy(this.luminary, newSceneId, copySource);
        }

        // Mark the new scene as "building" so the build preview blurs
        // it until the content it needs lands (set_text/set_media/image_ask).
        this.startSceneBuilding(newSceneId);
        // Creating the overlay also splits the base scene underneath it, so the
        // remainder of the scene group is a new base scene that needs blurring too.
        this.blurScenesCreatedSince(sceneIdsBefore, [newSceneId]);

        return {
            sceneId: newSceneId,
            isMotionGraphics: false,
            createdBaseScenes: this.someSceneCreatedSince(sceneIdsBefore, newSceneId),
        };
    }

    public async execute(args: AddVoiceoverSceneArgs, context: ToolExecutionContext) {
        const { scenes } = args;
        const { parentActionGroup } = context;

        const { sceneGroup } = resolveSceneGroupHandle(this.luminary, args);

        // Check if scene group has a transcript. transcriptTokens is the canonical interleaved
        // (word + pause) list the agent reads, so the words it passes back match token-for-token.
        const transcript = sceneGroup.originalTranscript;
        const transcriptTokens = getInterleavedTranscript(this.getState(), sceneGroup);

        if (!transcript || transcriptTokens.length === 0) {
            return `Error: Scene group ${sceneGroup.id} does not have a transcript. Only voiceover and talking-head scene groups with transcripts can have scenes added to them.`;
        }

        // cover_whole_group spans the entire group in one scene. It is refused once the group holds
        // ANY overlay — counting scenes isn't enough, because a prior cover_whole_group leaves a
        // single full-length overlay (the base is consumed frame 0 to end), which a scene-count gate
        // would wave through so a retry clobbers it. Gating on overlays keeps the "never blows away
        // work" promise: only the untouched auto base scene lets it through.
        if (scenes.some((scene) => scene.cover_whole_group)) {
            if (scenes.length > 1) {
                return `Error adding scene: cover_whole_group covers the entire group, so it must be the only scene in the request. Drop the other scenes, or give every scene its own words instead.`;
            }
            const existingOverlays = getOverlayScenesInSceneGroup(this.getState(), sceneGroup);
            if (existingOverlays.length > 0) {
                return `Error adding scene: cover_whole_group is only allowed while the scene group has no overlays yet — just its auto-generated base scene. This group already has ${existingOverlays.length} overlay scene(s) (a full-length audiogram a previous cover_whole_group placed counts as one), and covering the whole group would blow them away. Add targeted overlays with words instead — the base/waveform shows through everywhere they don't cover.`;
            }
        }

        const added: {
            sceneId: string;
            isMotionGraphics: boolean;
            createdBaseScenes: boolean;
            requestIndex: number;
            words: string[];
        }[] = [];
        const failures: string[] = [];

        // Resolved before any scene is placed: placing one can split the base scene and retire its id,
        // so a scene the batch copies more than once would be gone by the time its next turn came.
        const copySources = scenes.map(({ scene_design_id }) =>
            isSceneIdReference(scene_design_id)
                ? resolveSceneCopySource(this.luminary, this.getState(), scene_design_id)
                : undefined,
        );

        scenes.forEach(({ words, cover_whole_group, scene_design_id }, requestIndex) => {
            const describeRequest = `scenes[${requestIndex}] ("${scene_design_id}")`;
            try {
                const source = copySources[requestIndex];
                if (source && 'error' in source) {
                    failures.push(`${describeRequest}: ${source.error}`);
                    return;
                }
                // For cover_whole_group, reconstruct the words from the transcript the tool already
                // holds — the same tokens the caller would otherwise have to enumerate by hand — so
                // the resolved range spans frame 0 to the end.
                const effectiveWords = cover_whole_group
                    ? transcriptTokens.map(interleavedTokenToString)
                    : (words ?? []);
                const result = this.addOneScene(
                    sceneGroup,
                    effectiveWords,
                    source?.sceneDesign ?? scene_design_id,
                    parentActionGroup,
                    source,
                );
                if ('error' in result) {
                    failures.push(`${describeRequest}: ${result.error}`);
                    return;
                }
                added.push({ ...result, requestIndex, words: effectiveWords });
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Unknown error occurred';
                failures.push(`${describeRequest}: ${errorMessage}`);
            }
        });

        // One checkpoint for the whole batch — the scenes were added together and are undone together.
        if (added.length > 0) {
            this.dispatch(
                checkpointVideo({
                    video_data: saveRawLuminary(this.luminary),
                    systemTriggered: false,
                    parentActionGroup,
                }),
            );
        }

        return this.describeOutcome(added, failures);
    }

    /**
     * Report the batch back to the agent: every added scene by the scene_id the edit tools address
     * it by, plus any request that was refused, named so a retry can target just that one.
     */
    private describeOutcome(
        added: {
            sceneId: string;
            isMotionGraphics: boolean;
            createdBaseScenes: boolean;
            requestIndex: number;
            words: string[];
        }[],
        failures: string[],
    ): string {
        if (added.length === 0) {
            return `Error adding scene: ${failures.join('; ')}`;
        }

        const shortIds = computeSceneShortIds(this.luminary.scenes.map((scene) => scene.id));
        const shortIdOf = (sceneId: string) => shortIds.get(sceneId) ?? sceneId;

        // A single scene names the words it covers, as it always has. A batch identifies each
        // scene by the request slot it came from instead — echoing every range back would replay
        // most of the transcript once a batch gets large.
        const summary =
            added.length === 1
                ? // A motion graphics scene is authored by its own follow-up tool, which is what the
                  // caller acts on next, so it is named by id alone rather than by the words it covers.
                  added[0].isMotionGraphics
                    ? `Success: Added an empty motion graphics scene (scene_id: ${shortIdOf(added[0].sceneId)}). It inherits the narration it covers, and the scene underneath stays as the base.`
                    : `Success: Added scene covering ${describeCoveredWords(added[0].words)} (scene_id: ${shortIdOf(added[0].sceneId)}).`
                : `Success: Added ${added.length} scenes:\n` +
                  added
                      .map(
                          (scene) =>
                              `  - scenes[${scene.requestIndex}] → scene_id ${shortIdOf(scene.sceneId)}`,
                      )
                      .join('\n');

        // set_text/set_media can't edit a motion graphics scene, so an all-motion-graphics batch is
        // not sent to them.
        const hasDesignedScenes = added.some((scene) => !scene.isMotionGraphics);
        const contentFollowUp = hasDesignedScenes
            ? `\n\nSet each scene's text/media with ${TOOL_NAMES.setText}/${TOOL_NAMES.setMedia} addressing it by scene_id. Match the content to the words it covers.`
            : '';
        const failureReport =
            failures.length > 0
                ? `\n\nNot added (fix and retry just these): ${failures.join('; ')}`
                : '';
        const baseSceneNote = added.some((scene) => scene.createdBaseScenes)
            ? addedSceneBaseSceneNote()
            : '';

        return `${summary}${contentFollowUp}${failureReport}${baseSceneNote}`;
    }
}
